// @flow

import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { time, dateDelta } from '../Shared/timeutils'
import DateTimeFilter from '../Shared/Buttons/DateTimeFilter'
import CustomFilter from '../Shared/Buttons/CustomFilter'
import FilterBy from '../Shared/Filters/FilterBy'
import ContentPost from '../Shared/Data/ContentPost'
import PushNotifications from '../Shared/Data/PushNotifications'
import MediumInfoBlock from '../Shared/Data/MediumInfoBlock'
import { fetchContentDetails } from '../Redux/Actions/Details'
import { fetchContentImpressionStats } from '../Redux/Actions/ContentImpression'
import BackArrow from '../Shared/Icons/icon-backarrow-grey.svg'
import './ContentDetails.css'
import './Wrappers.css'
import { componentCreated, updateDateFilter, updateGroupFilter } from '../Redux/Actions/DateFilter'
import UsersMentioned from '../Shared/Data/UsersMentioned'
import CommentsAndReactionsBlock from '../Shared/Data/CommentsAndReactionsBlock'
import ContentHashtags from '../Shared/Data/ContentHashtags'
import { useLocation, useNavigate } from 'react-router'
import { getDateFilter, getGroupFilter, processDate, processGroup, composeFilterString } from '../utils/analytics-utils'
import { addQuery } from '../utils/router-utils'
import ContentImpression from '../Shared/Display/ContentImpression'

function ContentDetails () {
  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const id = useSelector(state => state.account.ID)
  const data = useSelector(state => state.details.content)
  const dateFilterStore = useSelector(state => state.dateFilter)
  const name = 'ContentDetails'

  let dateFilterFromStore = getDateFilter(dateFilterStore, name)
  dateFilterFromStore = dateFilterFromStore ? dateFilterFromStore.dateFilter || 'Week' : 'Week'
  const [dateFilter, setDateFilter] = useState(dateFilterFromStore)

  const groupFilterFromStore = getGroupFilter(dateFilterStore, name) || ''
  const [groupFilter, setGroupFilter] = useState(groupFilterFromStore)
  const dateInterval = dateFilter ? dateFilter.dateInterval || null : null

  useEffect(() => {
    dispatch(componentCreated(name))
  }, [])

  useEffect(() => {
    let contentId = ''
    const searchQ = location.search.substring(1).split('&')
    searchQ.forEach((q) => {
      if (q.split('=')[0] === 'id') {
        contentId = q.split('=')[1]
      }
    })
    const date = processDate(dateFilter)
    const group = processGroup(groupFilter)
    dispatch(fetchContentDetails(id, date.start.toISOString(), date.end.toISOString(), contentId, { group: group.groups, profile: group.profiles, appType: group.alt }))
  }, [])

  useEffect(() => {
    newQuery(location.search.substring(1).split('&'))
  }, [])

  const newQuery = (query) => {
    const qobj = {}
    let newDateFilter
    query.forEach((q) => {
      qobj[q.split('=')[0]] = q.split('=')[1]
    })
    if (dateFilter === 'Custom' && dateInterval) {
      newDateFilter = dateInterval
    } else {
      newDateFilter = dateFilter
    }
    update(processDate(newDateFilter), processGroup(groupFilter), id)
  }

  const changeDateFilter = (newFilter, isCustom) => {
    if (isCustom) {
      dispatch(updateDateFilter(name, 'Custom', newFilter))
    } else {
      dispatch(updateDateFilter(name, newFilter))
    }
    const f = `${composeFilterString(groupFilter.profiles, groupFilter.groups)}_${groupFilter.alt || ''}`
    addQuery({ date: newFilter, filter: f }, location, navigate)
    setDateFilter(newFilter)
    if (newFilter !== 'Custom') {
      update(processDate(newFilter), processGroup(groupFilter))
    }
  }

  const changeGroupFilter = (newFilter) => {
    let newDateFilter
    if (dateFilter === 'Custom' && dateInterval) {
      newDateFilter = dateInterval
    } else {
      newDateFilter = dateFilter
    }

    dispatch(updateGroupFilter(name, newFilter))
    const f = `${composeFilterString(newFilter.profiles, newFilter.groups)}_${newFilter.alt || ''}`
    addQuery({ date: newDateFilter, filter: f }, location, navigate)
    setGroupFilter(newFilter)
    update(processDate(newDateFilter), processGroup(newFilter))
  }

  const update = (date, group, newID) => {
    const ID = newID || id
    const searchQ = location.search.substring(1).split('&')
    searchQ.forEach((q) => {
      searchQ[q.split('=')[0]] = q.split('=')[1]
    })

    // Adjust start and end times to the start and end of the day
    const adjustedStart = new Date(date.start)
    adjustedStart.setHours(0, 0, 0, 0) // Set to 12:00 AM

    const adjustedEnd = new Date(date.end)
    adjustedEnd.setHours(23, 59, 59, 999) // Set to 11:59:59 PM

    // Fetch content details
    dispatch(fetchContentDetails(ID, adjustedStart.toISOString(), adjustedEnd.toISOString(), searchQ.id, {
      group: group.groups,
      profile: group.profiles,
      appType: group.alt
    }))

    // Fetch content-specific impression stats
    dispatch(fetchContentImpressionStats(ID, adjustedStart.toISOString(), adjustedEnd.toISOString(), [searchQ.id], {
      group: group.groups,
      profile: group.profiles,
      appType: group.alt
    }))
  }

  return (
    <div className='ContentDetails'>
      <div className='toolbar'>
        <div>
          <h2>
            <div className='button-cursor'>
              <img src={BackArrow} className='interactive' alt='back' onClick={() => navigate(-1)} />
              Content Overview
            </div>
          </h2>
        </div>
        <DateTimeFilter filterBy={changeDateFilter} currentFilter={dateFilter} />
        <div />
      </div>
      {(dateFilter !== 'Day' && dateFilter !== 'Week' && dateFilter !== 'Month') ? <CustomFilter filterBy={changeDateFilter} currentFilter={dateFilter} /> : ''}
      <div className='ContentInternal ContentContainer'>
        <div className='col1'>
          <ContentPost title={data.title} date={data.created} thumbnailUrl={data.thumbnailUrl} />
          <PushNotifications pushOnOff={data.notify} />
          <UsersMentioned mentions={data.mentions} loading={data.title === 'Loading'} />
          <ContentHashtags hashtags={data.hashtags} loading={data.title === 'Loading'} />
        </div>
        <div className='colbig col2'>
          <div className='filterInGrid'>
            <FilterBy
              changeFilter={changeGroupFilter}
              showProfiles
              showGroups
              type='App'
              currentFilter={processGroup(groupFilter)}
            />
            <ContentImpression />
          </div>
          <div className='firstColGrid'>
            <div>
              <MediumInfoBlock title='TOTAL VIEWS' value={data.totalViews} delta={data.totalViewsPctChange} sinceText={dateDelta(dateFilter)} />
            </div>
            <div>
              <MediumInfoBlock title='TIMES SHARED' value={data.totalShares} delta={data.totalSharesPctChange} sinceText={dateDelta(dateFilter)} />
            </div>
            <div>
              <MediumInfoBlock title='ACCESSED THROUGH NOTIFICATIONS' value={`${(data.totalViewsNotifications || 0)}`} delta={data.totalViewsNotificationsPctChange} sinceText={dateDelta(dateFilter)} />
            </div>
            <div>
              <MediumInfoBlock title='OFFLINE DOWNLOADS' value={data.totalOfflineDownloads} delta={data.totalOfflineDownloadsPctChange} sinceText={dateDelta(dateFilter)} />
            </div>
          </div>
          <div className='secondColGrid'>
            <div>
              <MediumInfoBlock title='AVERAGE DURATION IN WEB' value={time(data.avgDurationInCMS)} delta={data.pctChangedInCMS || 0} sinceText={dateDelta(dateFilter)} />
            </div>
            <div>
              <MediumInfoBlock title='AVERAGE DURATION IN MOBILE' value={time(data.avgDurationInApp)} delta={data.pctChangedInApp || 0} sinceText={dateDelta(dateFilter)} />
            </div>
            <div>
              <MediumInfoBlock title='UNIQUE VIEWS' value={data.uniqueViews} delta={data.uniqueViewsPctChange} sinceText={dateDelta(dateFilter)} />
            </div>
            <div>
              <CommentsAndReactionsBlock loading={data.title === 'Loading'} title={data.accountDetails && data.accountDetails.ui_version === 'v1' ? 'NUMBER OF COMMENTS & REACTIONS' : 'NUMBER OF COMMENTS & LIKES' } commentsEnabled={data.commentsEnabled} likesEnabled={data.likesEnabled} totalComments={data.totalComments} totalLikes={data.totalLikes} totalReactions={data.totalReactions} accountDetails ={ data.accountDetails || {}} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContentDetails
